import React, { useState, useEffect } from 'react';
import api from '../api';
import { Award, Download, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * StudentCertificates — shows a student's earned certificates as clickable cards.
 * Each card triggers a PDF download.
 */
export const StudentCertificates = ({ vertical = '' }) => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = vertical ? `/certificates/mine?vertical=${encodeURIComponent(vertical)}` : '/certificates/mine';
        const res = await api.get(path);
        if (!cancelled) setCerts(res.data?.certificates || []);
      } catch {
        if (!cancelled) setCerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vertical]);

  const download = async (cert) => {
    try {
      const res = await api.get(`/certificates/${cert.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CodeITT_${cert.cert_type}_${cert.certificate_number || cert.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download certificate');
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-400 py-4 text-center" data-testid="certs-loading">Loading certificates…</div>;
  }
  if (certs.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center" data-testid="certs-empty">
        <Award className="w-10 h-10 mx-auto text-slate-300 mb-2" />
        <p className="text-sm font-bold text-slate-500">No certificates yet</p>
        <p className="text-xs text-slate-400 mt-1">Certificates unlock at 10, 25, 50, 75 & 100 classes + on module completion.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="certs-grid">
      {certs.map((c) => (
        <div
          key={c.id}
          className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => download(c)}
          data-testid={`cert-card-${c.id}`}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: c.cert_type === 'module' ? 'linear-gradient(135deg,#F59E0B,#DC2626)' : 'linear-gradient(135deg,#8B5CF6,#0EA5E9)' }}
            >
              {c.cert_type === 'module' ? <Sparkles className="w-6 h-6 text-white" /> : <Award className="w-6 h-6 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{c.cert_type === 'module' ? 'Module Certificate' : 'Achievement'}</p>
              <p className="text-sm font-extrabold text-[#0F172A] truncate">{c.milestone_label}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{c.module || 'General'}</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400">#{c.certificate_number}</span>
            <span className="text-xs font-bold text-indigo-600 flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> PDF
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StudentCertificates;
